import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import { TitleWithTooltipColumns } from 'components/TitleWithTooltipColumns';
import type { ActKc3LevelEnum, ActKc3RegistryItem } from 'gql/graphql';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import { formatDateToLocale, getCheckedIdsArr } from 'utils';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useCallback, useMemo, useState } from 'react';

import { uerContractActsKC3DateFilterGql, uerContractActsKC3FullNumberFilterGql } from '../actKC3ListQueries';
import { ActKc2OrKc3Field, NumberColumnRenderer } from '../cellRenderers';
import type { ActListItemWithGroupInfo, ColumnsCommonProps } from '../types';

import { MenuRenderer } from './cellRenderers';

type TColumns = ColumnsCommonProps & {
    contractId: number;
    changeFilterHandler: ({
        fieldName,
        checkedValues,
        fullDataSet,
    }: {
        fieldName: 'number' | 'actKC3Date';
        checkedValues: any;
        fullDataSet: any;
    }) => void;
    costRounding: number;
    refreshListActListData: () => void;
    onActKc3Add: (data: ActListItemWithGroupInfo) => void;
    actKC3Level?: ActKc3LevelEnum;
};

const GetTooltipHeader = (title: string, childTooltip: string) => () =>
    TitleWithTooltipColumns({
        title,
        childTooltip,
        style: {
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
        },
    });

export const useActKC3ListColumns = ({
    contractId,
    changeCollapseState,
    collapseState,
    changeFilterHandler,
    costRounding,
    refreshListActListData,
    onActKc3Add,
    actKC3Level,
}: TColumns) => {
    const filterVariables = useMemo(() => ({ contractId }), [contractId]);
    const [selection, , , setSelection] = useSelection('actKc3List');
    const [pinnedItems, , , setPinned] = usePin('actKc3List');
    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);
    const [checkedRows, setCheckedRows] = useState<Record<number, boolean>>({});
    const [singleDeleteItemData, setSingleDeleteItemData] = useState<ActListItemWithGroupInfo | undefined>(undefined);
    const [kc3ForTotalsData, setKc3ForTotalsData] = useState<ActKc3RegistryItem | null>(null);

    const checkRowHandler = useCallback(({ id, checked }: { id: number; checked: boolean }) => {
        setCheckedRows((prev) => ({ ...prev, [id]: checked }));
    }, []);

    const openSingleDelete = useCallback((data: ActListItemWithGroupInfo) => {
        setSingleDeleteItemData(data);
    }, []);
    const setDataForTotals = useCallback((kc3?: ActKc3RegistryItem) => {
        setKc3ForTotalsData(kc3 ?? null);
    }, []);
    const checkedRowsArr = useMemo(
        () => (singleDeleteItemData ? [] : getCheckedIdsArr(checkedRows)),
        [checkedRows, singleDeleteItemData],
    );
    const resetCheckedRowsHandler = useCallback(() => {
        setCheckedRows({});
        setSingleDeleteItemData(undefined);
    }, []);

    const columns = useMemo<(ColDef<ActListItemWithGroupInfo> | ColGroupDef<ActListItemWithGroupInfo>)[]>(
        () => [
            {
                field: 'menu' as any,
                headerName: '',
                cellRenderer: MenuRenderer,
                cellRendererParams: {
                    checkRowHandler,
                    checkedRows,
                    openSingleDelete,
                    refreshListActListData,
                    setDataForTotals,
                    onActKc3Add,
                    actKC3Level,
                },
                width: 110,
                pinned: 'left',
                lockPosition: 'left',
                resizable: false,
                cellClass: 'center-aligned-cell',
            },
            {
                field: 'number' as any,
                headerName: 'Номер',
                cellRenderer: NumberColumnRenderer,
                cellRendererParams: { changeCollapseState, collapseState },
                flex: 2,
                minWidth: 100,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerContractActsKC3FullNumberFilter',
                    query: uerContractActsKC3FullNumberFilterGql,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            headerName: 'Номер',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                field: 'actKC3Date',
                headerName: 'Дата',
                flex: 1,
                width: 115,
                cellRenderer: ActKc2OrKc3Field,
                cellRendererParams: {
                    kc2Field: 'actKC2Date',
                    kc3Field: 'actKC3Date',
                    isDate: true,
                },
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    withSearch: false,
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerContractActsKC3DateFilter',
                    query: uerContractActsKC3DateFilterGql,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            headerName: 'Дата',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                            cellRenderer: ({ value }: { value: Date }) => formatDateToLocale(value),
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                field: 'date' as any,
                headerName: 'Отчетный период',
                marryChildren: true,
                children: [
                    {
                        field: 'actKC3ReportingStartDate',
                        headerName: 'Начало',
                        flex: 1,
                        width: 115,
                        cellRenderer: ActKc2OrKc3Field,
                        cellRendererParams: {
                            kc2Field: 'actKC2ReportingStartDate',
                            kc3Field: 'actKC3ReportingStartDate',
                            isDate: true,
                        },
                    },
                    {
                        field: 'actKC3ReportingFinishDate',
                        headerName: 'Окончание',
                        flex: 1,
                        width: 115,
                        cellRenderer: ActKc2OrKc3Field,
                        cellRendererParams: {
                            kc2Field: 'actKC2ReportingFinishDate',
                            kc3Field: 'actKC3ReportingFinishDate',
                            isDate: true,
                        },
                    },
                ],
            },
            {
                field: 'costs' as any,
                headerName: 'Стоимость выполненных работ и затрат,руб',
                marryChildren: true,
                children: [
                    {
                        field: 'actKC3FromWorkStartCost',
                        headerName: 'С начала проведения работ',
                        flex: 1,
                        width: 205,
                        headerComponent: GetTooltipHeader('С начала проведения работ', 'С начала проведения работ'),
                        cellRenderer: ActKc2OrKc3Field,
                        cellRendererParams: {
                            kc3Field: 'actKC3FromWorkStartCost',
                            costRounding,
                        },
                        cellClass: 'ag-right-aligned-cell',
                    },
                    {
                        field: 'actKC3FromYearStartCost',
                        headerName: 'С начала года',
                        flex: 1,
                        width: 205,
                        cellRenderer: ActKc2OrKc3Field,
                        cellRendererParams: {
                            kc3Field: 'actKC3FromYearStartCost',
                            costRounding,
                        },
                        cellClass: 'ag-right-aligned-cell',
                    },
                    {
                        field: 'actKC3ReportedPeriodCost',
                        headerName: 'В том числе за отчетный период',
                        headerComponent: GetTooltipHeader(
                            'В том числе за отчетный период',
                            'В том числе за отчетный период',
                        ),
                        flex: 1,
                        width: 205,
                        cellRenderer: ActKc2OrKc3Field,
                        cellRendererParams: {
                            kc2Field: 'actKC2TotalCost',
                            kc3Field: 'actKC3ReportedPeriodCost',
                            costRounding,
                        },
                        cellClass: 'ag-right-aligned-cell',
                    },
                ],
            },
            {
                field: 'actKC3TotalCost' as any,
                headerName: 'Всего с НДС',
                flex: 1,
                width: 150,
                cellRenderer: ActKc2OrKc3Field,
                cellRendererParams: {
                    kc2Field: 'actKC2TotalCostWithTax',
                    kc3Field: 'actKC3TotalCostWithTax',
                    costRounding: 2,
                },
                cellClass: 'ag-right-aligned-cell',
            },
        ],
        [
            changeCollapseState,
            changeFilterHandler,
            checkRowHandler,
            checkedRows,
            collapseState,
            costRounding,
            filterVariables,
            onActKc3Add,
            openSingleDelete,
            refreshListActListData,
            setDataForTotals,
            actKC3Level,
        ],
    );

    const columnsForSettings = useMemo<any[]>(
        () =>
            columns.reduce((acc: ColDef[], item, idx) => {
                if (idx > 0) {
                    const newC = { ...item } as ColDef & { disabled: boolean };

                    if (idx < 2) {
                        newC.disabled = true;
                    }

                    if (pinnedItems[getColumnId(item)]) {
                        newC.pinned = true;
                    }

                    acc.push(newC);
                }

                return acc;
            }, []),
        [columns, pinnedItems],
    );

    const handleTableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: columnsForSettings,
                incomingColumns: visible,
                setPinned,
                setSelection,
            });
        },
        [columnsForSettings, setPinned, setSelection],
    );

    const fullSetColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    return {
        columns: fullSetColumns,
        columnsForSettings,
        handleTableSettingsConfirm,
        isColumnVisible,
        checkedRowsArr,
        resetCheckedRowsHandler,
        singleDeleteItemData,
        kc3ForTotalsData,
        closeKc3Totals: setDataForTotals,
    };
};
